import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ScrollView, TouchableOpacity, View, Text } from 'react-native'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import { getDatePickerLocale } from '@/utils/datepickerLocale'
import 'react-datepicker/dist/react-datepicker.css'

import {
  AppBackground,
  BottomActionContainer,
  Button,
  Drop,
  Input,
  ResponsiveLogo
} from '@ui/components'

import {
  InputContainer,
  InputWrapper,
  Headline2,
  Headline5,
  BodyText,
  ErrorText,
  colors,
  DatePickerWrapper,
  DisabledButton,
  ButtonContent,
  DisabledButtonText
} from '@/aena/styles' // Importando los estilos del PetAdd

import { ReactComponent as PawIcon } from '@/assets/icons/paw_color.svg'
import { usePetInsurance } from '@front/logic'
import { addExternalInsuranceAPI, InsuranceCompany } from '@front/apis'
import { useAppSelector, setLoading, useAppDispatch } from '@front/store'
import Toast from 'react-native-toast-message'
import { t } from 'i18next'
import { PETPASS_ExternalInsuranceType, PETPASS_insurance_recurrence } from '@/db/schema/types'
import { HeaderWithBackAena } from '@/components/headerwithback'
import CheckBoxCustom from '@/components/customcheckbox'


interface ExternalInsuranceForm {
  photoPdf: File | null
  insuranceCompany: string
  policyNumber: string
  insuranceType?: PETPASS_ExternalInsuranceType
  paymentRecurrence?: PETPASS_insurance_recurrence
  expirationDate: string
  acceptsConditions: boolean
}


export const ExternalInsuranceAdd: FC = () => {
  const PAYMENT_RECURRENCE = [
    { id: PETPASS_insurance_recurrence.MONTHLY, title: t('insurance.payment.monthly'), data: {} },
    { id: PETPASS_insurance_recurrence.QUARTERLY, title: t('insurance.payment.quarterly'), data: {} },
    { id: PETPASS_insurance_recurrence.ANNUAL, title: t('insurance.payment.annual'), data: {} },
  ]
  const INSURANCE_TYPES = [
    { id: PETPASS_ExternalInsuranceType.CIVIL_LIABILITY, title: t('insurance.types.liability'), data: {} },
    { id: PETPASS_ExternalInsuranceType.VETERINARY_LIABILITY, title: t('insurance.types.veterinary_liability'), data: {} },
    { id: PETPASS_ExternalInsuranceType.HOME_LIABILITY, title: t('insurance.types.home_liability'), data: {} },
    { id: PETPASS_ExternalInsuranceType.OTHER, title: t('insurance.types.other'), data: {} },
  ]
    
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { getInsuranceCompanies } = usePetInsurance()
  const dispatch = useAppDispatch()
  const { guid } = useAppSelector((state) => state.selectedPet)

  const [formData, setFormData] = useState<ExternalInsuranceForm>({
    photoPdf: null,
    insuranceCompany: '',
    policyNumber: '',
    insuranceType: undefined,
    paymentRecurrence: undefined,
    expirationDate: '',
    acceptsConditions: true
  })

  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
  const [selectedFileName, setSelectedFileName] = useState<string>('')

  


  const [companies, setCompanies] = useState<InsuranceCompany[]>([])

  const fetchCompanies = async () => {
    const res = await getInsuranceCompanies()
    setCompanies(res)
  }

  useEffect(() => {
    fetchCompanies()
  }, [])


  const dropCompanies = useMemo(() => {
    return companies.sort((a, b) => a.order - b.order).map((c) => ({
      id: c.guid,
      title: c.name,
      data: {},
    }))
  }, [companies])


  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'application/pdf') {
      setFormData(prev => ({ ...prev, photoPdf: file }))
      setSelectedFileName(file.name)
    } else if (file) {
      alert(t('insurance.errors.pdf_only'))
    }
  }

  const handleInputChange = (field: keyof ExternalInsuranceForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const canSubmit = useMemo(() => {
    return formData.photoPdf && formData.insuranceCompany && formData.policyNumber && formData.insuranceType && formData.paymentRecurrence && formData.expirationDate && formData.acceptsConditions
  }, [formData])

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!formData.photoPdf) {
      errors.photoPdf = t('insurance.errors.pdf_required')
    }

    if (!formData.insuranceCompany) {
      errors.insuranceCompany = t('insurance.errors.company_required')
    }

    if (!formData.policyNumber?.trim()) {
      errors.policyNumber = t('insurance.errors.policy_number_required')
    }

    if (!formData.insuranceType) {
      errors.insuranceType = t('insurance.errors.insurance_type_required')
    }

    if (!formData.paymentRecurrence) {
      errors.paymentRecurrence = t('insurance.errors.payment_recurrence_required')
    }

    if (!expirationDate) {
      errors.expirationDate = t('insurance.errors.expiration_date_required')
    }

    if (!formData.acceptsConditions) {
      errors.acceptsConditions = t('insurance.errors.accept_conditions_required')
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    dispatch(setLoading(true))
    try {
      console.log('formData----', { formData })
      if (!formData.insuranceType) {
        throw new Error('Insurance type is required')
      }
      if (!formData.paymentRecurrence) {
        throw new Error('Payment recurrence is required')
      }

      const res = await addExternalInsuranceAPI({
        guid,
        payload: {
          file: formData.photoPdf,
          type: formData.insuranceType,
          policyNumber: formData.policyNumber,
          expirationDate: formData.expirationDate,
          // coverage: formData.coverageAmount,
          //terms://
          // price: formData.receiptAmount,
          companyGUID: formData.insuranceCompany,
          recurrence: formData.paymentRecurrence,
          confirm_veracity: formData.acceptsConditions

        },
      })

      Toast.show({
        type: 'success',
        text1: t('insurance.success.added_title'),
        text2: res.message,
      })

      navigate(-1)

    } catch (error: any) {
      console.error('Error al guardar seguro externo:', error)
      Toast.show({
        type: 'error',
        text1: t('insurance.errors.add_error_title'),
        text2: error.message,
      })
    } finally {
      dispatch(setLoading(false))
    }
  }



  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena title={t('insurance.external.add_title')} />

        <View style={{ marginBottom: 1 }}>
          <Headline2 style={{ padding: 15, color: colors.azulCorporativo, borderRadius: 10 }}>
            {t('insurance.external.information_title')}
          </Headline2>
        </View>

        <BottomActionContainer>
            {/* Foto PDF */}
            <InputWrapper>
              <Headline5>{t('insurance.external.policy_pdf_label')} *</Headline5>
              <InputContainer>
                <TouchableOpacity
                  onPress={() => fileInputRef.current?.click()}
                  style={{
                    alignItems: 'center',
                    padding: 10,
                    borderWidth: 2,
                    borderColor: '#ccc',
                    borderRadius: 8,
                    backgroundColor: '#f8f8f8',
                    borderStyle: 'dashed',
                  }}
                >
                  <PawIcon width={48} height={48} />
                  <BodyText>
                    {selectedFileName || t('insurance.external.select_pdf_file')}
                  </BodyText>
                </TouchableOpacity>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                {fieldErrors.photoPdf && <ErrorText>{fieldErrors.photoPdf}</ErrorText>}
              </InputContainer>
            </InputWrapper>

            {/* Compañía Aseguradora */}
            <InputWrapper>
              <Headline5>{t('insurance.external.company_label')} *</Headline5>
              <InputContainer>
                <Drop
                  buttonText={t('common.continue')}
                  data={dropCompanies}
                  active={dropCompanies.findIndex(c => c.id === formData.insuranceCompany)}
                  selectedItem={dropCompanies.findIndex(c => c.id === formData.insuranceCompany)}
                  onPress={(item) => handleInputChange('insuranceCompany', item.id)}
                  placeholder={t('insurance.external.select_company')}
                  icon={<PawIcon />}
                  color="aena"
                  
                  variant="outlined"
                />
                {fieldErrors.insuranceCompany && <ErrorText>{fieldErrors.insuranceCompany}</ErrorText>}
              </InputContainer>
            </InputWrapper>

            {/* Tipo de Seguro y Recurrencia de Pago */}
            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <Headline5>{t('insurance.external.type_label') + ' *'}</Headline5>
                <InputContainer>
                  <Drop
                    buttonText={t('common.continue')}
                    data={INSURANCE_TYPES}
                    active={INSURANCE_TYPES.findIndex(c => c.id === formData.insuranceType)}
                    selectedItem={INSURANCE_TYPES.findIndex(c => c.id === formData.insuranceType)}
                    onPress={(item) => handleInputChange('insuranceType', item.id)}
                    placeholder={t('insurance.external.select_type')}
                    icon={<PawIcon />}
                    variant="outlined"
                    color="aena"
                  />
                  {fieldErrors.insuranceType && <ErrorText>{fieldErrors.insuranceType}</ErrorText>}
                </InputContainer>
              </View>
              <View style={{ flex: 1 }}>
                <Headline5>{t('insurance.external.payment_recurrence_label') + ' *'}</Headline5>
                <InputContainer>
                  <Drop
                    buttonText={t('common.continue')}
                    data={PAYMENT_RECURRENCE}
                    active={PAYMENT_RECURRENCE.findIndex(c => c.id === formData.paymentRecurrence)}
                    selectedItem={PAYMENT_RECURRENCE.findIndex(c => c.id === formData.paymentRecurrence)}
                    onPress={(item) => handleInputChange('paymentRecurrence', item.id)}
                    placeholder={t('insurance.external.select_recurrence')}
                    icon={<PawIcon />}
                    variant="outlined"
                    color="aena"
                  />
                  {fieldErrors.paymentRecurrence && <ErrorText>{fieldErrors.paymentRecurrence}</ErrorText>}
                </InputContainer>
              </View>
              
            </View>

            {/* Número de Póliza */}
            <InputWrapper>
                <Headline5>{t('insurance.external.policy_number_label') + ' *'}</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: formData.policyNumber,
                      onChangeText: (text: string) => handleInputChange('policyNumber', text),
                      placeholder: t('insurance.external.policy_number_placeholder'),
                    }}
                  />
                  {fieldErrors.policyNumber && <ErrorText>{fieldErrors.policyNumber}</ErrorText>}
                </InputContainer>
              </InputWrapper>

            {/* Fecha de Expiración */}
            <InputWrapper>
              <Headline5>{t('insurance.external.expiration_date_label') + ' *'}</Headline5>
              <InputContainer>
                <DatePickerWrapper>
                  <DatePicker
                    selected={expirationDate}
                    onChange={(date: Date | null) => {
                      setExpirationDate(date)
                      handleInputChange('expirationDate', date?.toISOString() ?? '')
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t('insurance.external.select_date')}
                    className="datepicker-styled"
                    popperPlacement="bottom-start"
                    dropdownMode="select"
                    locale={getDatePickerLocale()}
                    minDate={new Date()}
                    showMonthDropdown
                    showYearDropdown
                  />
                </DatePickerWrapper>
                {fieldErrors.expirationDate && <ErrorText>{fieldErrors.expirationDate}</ErrorText>}
              </InputContainer>
            </InputWrapper>

        {canSubmit ? (
              <Button
                title={t('insurance.external.save_button')}
                icon={<PawIcon width={24} height={24} fill="#1a2732" />}
                size="block"
                textType="cta_medium"
                borderRadius={5}
                buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
                allowIconAndText
                color="primary"
                onPress={handleSubmit}
              />
            ) : (
              <DisabledButton disabled>
                <ButtonContent>
                  <PawIcon width={24} height={24} fill="#1a2732" />
                  <DisabledButtonText>{t('insurance.external.save_button')}</DisabledButtonText>
                </ButtonContent>
              </DisabledButton>
            )
          }
        </BottomActionContainer>
      </View>
    </AppBackground>
  )
}

export default ExternalInsuranceAdd